<script setup lang="ts">
  import type { Component, HTMLAttributes } from "vue";
  import type { RouteLocationRaw } from "vue-router";
  import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-vue-next";
  import { cn } from "@/lib/utils";

  type BannerVariant = "info" | "warning" | "error" | "ok";

  const props = withDefaults(
    defineProps<{
      variant?: BannerVariant;
      message?: string;
      to?: RouteLocationRaw;
      actionLabel?: string;
      class?: HTMLAttributes["class"];
    }>(),
    { variant: "warning", message: undefined, to: undefined, actionLabel: "Go to the new version →", class: undefined },
  );

  const VARIANTS: Record<BannerVariant, { icon: Component; class: string }> = {
    info: { icon: Info, class: "border-status-info-border bg-status-info-soft text-status-info-strong" },
    warning: { icon: TriangleAlert, class: "border-status-warning-border bg-status-warning-soft text-status-warning-strong" },
    error: { icon: CircleAlert, class: "border-status-error-border bg-status-error-soft text-status-error-strong" },
    ok: { icon: CircleCheck, class: "border-status-success-border bg-status-success-soft text-status-success-strong" },
  };
</script>

<template>
  <div :class="cn('flex items-center gap-3 rounded-lg border px-4 py-3 text-sm', VARIANTS[variant].class, props.class)">
    <component
      :is="VARIANTS[variant].icon"
      class="size-4 shrink-0" />
    <span><slot>{{ message }}</slot></span>
    <slot name="action">
      <RouterLink
        v-if="to !== undefined"
        :to
        class="ml-auto shrink-0 font-medium underline underline-offset-2">
        {{ actionLabel }}
      </RouterLink>
    </slot>
  </div>
</template>
